import { lazy, Suspense, startTransition, useEffect } from "react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { ScrollToTopButton } from "@/components/ui/ScrollToTopButton";
import { ProductGrid } from "@/components/ProductGrid";
import { DetailedLoanCard } from "@/components/DetailedLoanCard";
import { VirtualizedProductList } from "@/components/VirtualizedProductList";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PageSkeleton } from "@/components/ui/PageSkeleton";
import { DeferredSection } from "@/components/DeferredSection";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import { topPrivateLoans, topSavings, topBusinessLoans } from "@/data/featuredProducts";
import { samblaData } from "@/data/providers/sambla";
import { lanekollData } from "@/data/providers/lanekoll";
import { tryggaData } from "@/data/providers/trygga";
import { TopGuidesSection } from "@/components/TopGuidesSection";
import kreaLogo from "@/assets/krea-logo.webp";
import frodaLogo from "@/assets/Froda_stor.webp";
import capitalBoxLogo from "@/assets/CapitalBox_stor.webp";
import HeroSection from "@/components/HeroSection";
import { deferScript } from "@/utils/deferScript";
import { scheduleIdleTask } from "@/utils/taskScheduler";
import { MortgagePage, PrivateLoanPage, BusinessLoanPage, LoanWithoutUCPage } from "@/routes/preloadableRoutes";

// Lazy load non-critical components - defer until after paint
const GoalsSection = lazy(() => import("@/components/GoalsSection"));
const TrustSection = lazy(() => import("@/components/TrustSection"));

const Index = () => {
  const navigate = useNavigate();
  const { currentLanguage } = useLanguage();

  // Defer non-critical operations to reduce main-thread blocking
  useEffect(() => {
    deferScript(() => {
      // Preconnect to external domains for faster resource loading
      const preconnectLinks = [
        'https://fonts.googleapis.com',
        'https://fonts.gstatic.com'
      ];
      
      preconnectLinks.forEach(href => {
        const link = document.createElement('link');
        link.rel = 'preconnect';
        link.href = href;
        link.crossOrigin = 'anonymous';
        if (!document.querySelector(`link[href="${href}"]`)) {
          document.head.appendChild(link);
        }
      });
    });

    // Preload key pages during idle time for instant navigation
    scheduleIdleTask(() => {
      MortgagePage.preload();
    });
    
    scheduleIdleTask(() => {
      PrivateLoanPage.preload();
    });
    
    scheduleIdleTask(() => {
      BusinessLoanPage.preload();
    });
    
    scheduleIdleTask(() => {
      LoanWithoutUCPage.preload();
    });
  }, []);

  // Prepare loan products for virtualized list
  const loanWithoutUCProducts = [
    {
      provider: "Loanstep",
      logo: "",
      loanAmount: "10 000 – 50 000 kr",
      nominalRate: "19,95 – 21,95 %",
      effectiveRate: "21,60 – 42,52 %",
      loanTerm: "14 dagar – 3 år",
      loanWithoutUC: "Ja",
      responseTime: "Direkt på skärmen",
      paymentSolution: "Banköverföring",
      link: `/${currentLanguage}/lana-utan-uc/loanstep`,
      rating: 4.7,
      reviewCount: 3502
    },
    {
      provider: "Fairlo",
      logo: "",
      loanAmount: "3 000 – 70 000 kr",
      nominalRate: "22,00 %",
      effectiveRate: "23,99 – 43,43 %",
      loanTerm: "1 mån – 6 år",
      loanWithoutUC: "Ja",
      responseTime: "Direkt på skärmen",
      paymentSolution: "Zimpler",
      link: `/${currentLanguage}/lana-utan-uc/fairlo`,
      rating: 4.6,
      reviewCount: 4333
    },
    {
      provider: "Ferratum",
      logo: "",
      loanAmount: "1 000 – 45 000 kr",
      nominalRate: "22,00 %",
      effectiveRate: "41,18 – 52,57 %",
      loanTerm: "Flexibel",
      loanWithoutUC: "Ja",
      responseTime: "Direkt på skärmen",
      paymentSolution: "Trustly",
      link: `/${currentLanguage}/lana-utan-uc/ferratum`,
      rating: 4.4,
      reviewCount: 1114,
      lenderBadge: "Beviljar flest"
    }
  ];

  return <div className="min-h-screen flex flex-col">
      <Navigation />
      <main className="flex-1">
        {/* Hero loads immediately - critical for LCP */}
        <HeroSection />

        {/* Goals section loads next */}
        <Suspense fallback={<div className="h-96 bg-background animate-pulse" />}>
          <GoalsSection />
        </Suspense>
        
        {/* Top 3 Privatlån - immediate render for above-the-fold content */}
        <section className="py-12 md:py-16 bg-muted/30">
          <div className="container">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8 gap-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-2">Privatlån</h2>
              </div>
              <Button variant="outline" onClick={() => navigate(`/${currentLanguage}/privatlan`)} className="gap-2 hover:bg-primary hover:text-primary-foreground hover:border-primary whitespace-nowrap">
                <span className="hidden sm:inline">Jämför fler privatlån</span>
                <span className="sm:hidden">Se fler</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[samblaData, lanekollData, tryggaData].map((provider) => {
                if (!provider) return null;

                const getTermValue = (term: string): string => {
                  const item = provider.termsAndConditions?.find(t => t.term === term);
                  return item?.value || "N/A";
                };

                // Bestäm lenderBadge baserat på provider
                let lenderBadge = "";
                if (provider.slug === 'sambla') {
                  lenderBadge = "Jämför 40 långivare";
                } else if (provider.slug === 'lanekoll') {
                  lenderBadge = "Jämför 20 långivare";
                } else if (provider.slug === 'trygga') {
                  lenderBadge = "Jämför 32 långivare";
                }

                return (
                  <DetailedLoanCard
                    key={provider.slug}
                    provider={provider.name}
                    logo={provider.logo}
                    loanAmount={getTermValue("Lånebelopp")}
                    nominalRate={getTermValue("Nominell ränta")}
                    effectiveRate={getTermValue("Effektiv ränta")}
                    loanTerm={getTermValue("Löptid")}
                    loanWithoutUC={getTermValue("Lån utan UC")}
                    responseTime={getTermValue("Svar på ansökan")}
                    paymentSolution="Banköverföring"
                    link={`/${currentLanguage}/privatlan/${provider.slug}`}
                    rating={provider.trustpilot?.rating}
                    reviewCount={provider.trustpilot?.reviews}
                    lenderBadge={lenderBadge}
                  />
                );
              })}
            </div>
          </div>
        </section>

        {/* Loan Without UC Section - Virtualized - Defer below fold */}
        <DeferredSection delay={0} rootMargin="300px">
          <section className="py-12 md:py-16">
            <div className="container">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-2">Lån utan UC</h2>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate(`/${currentLanguage}/lan-utan-uc`)} 
                  className="gap-2 hover:bg-primary hover:text-primary-foreground hover:border-primary whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Jämför fler lån utan UC</span>
                  <span className="sm:hidden">Se fler</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <VirtualizedProductList 
                products={loanWithoutUCProducts}
                itemHeight={440}
                virtualizeOnMobileOnly={true}
              />
            </div>
          </section>
        </DeferredSection>

        {/* Top 3 Företagslån - Deferred below fold */}
        <DeferredSection delay={0} rootMargin="400px">
          <section className="py-12 md:py-16 bg-muted/30">
            <div className="container">
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-8 gap-4">
                <div>
                  <h2 className="text-3xl md:text-4xl font-bold mb-2">Företagslån</h2>
                </div>
                <Button variant="outline" onClick={() => navigate(`/${currentLanguage}/foretagslan`)} className="gap-2 hover:bg-primary hover:text-primary-foreground hover:border-primary whitespace-nowrap">
                  <span className="hidden sm:inline">Jämför fler företagslån</span>
                  <span className="sm:hidden">Se fler</span>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <DetailedLoanCard
                  provider="Krea"
                  logo={kreaLogo}
                  loanAmount="10 000 – 30 000 000 kr"
                  nominalRate="Varierar mellan långivare"
                  effectiveRate="Varierar mellan långivare"
                  loanTerm="1 – 60 mån"
                  loanWithoutUC="Nej"
                  responseTime="Inom 24h"
                  paymentSolution="Banköverföring"
                  link={`/${currentLanguage}/foretagslan/krea`}
                  rating={4.8}
                  reviewCount={486}
                  lenderBadge="Jämför 30 långivare"
                />
                <DetailedLoanCard
                  provider="CapitalBox"
                  logo={capitalBoxLogo}
                  loanAmount="20 000 – 5 000 000 kr"
                  nominalRate="1,20 – 2,00 % mån"
                  effectiveRate="15,4 – 26,8 %"
                  loanTerm="1 – 48 mån"
                  loanWithoutUC="Nej"
                  responseTime="Inom 24h"
                  paymentSolution="Banköverföring"
                  link={`/${currentLanguage}/foretagslan/capitalbox`}
                  rating={4.4}
                  reviewCount={236}
                />
                <DetailedLoanCard
                  provider="Froda"
                  logo={frodaLogo}
                  loanAmount="Upp till 5 000 000 kr"
                  nominalRate="1,00 – 2,17 % mån"
                  effectiveRate="12,7 – 29,3 %"
                  loanTerm="Flexibel"
                  loanWithoutUC="Ja"
                  responseTime="Direkt"
                  paymentSolution="Banköverföring"
                  link={`/${currentLanguage}/foretagslan/froda`}
                  rating={4.9}
                  reviewCount={1665}
                />
              </div>
            </div>
          </section>
        </DeferredSection>

        {/* Viktigaste Guiderna */}
        <DeferredSection>
          <section className="py-6 md:py-10 bg-muted/30">
            <div className="container">
              <div className="text-center space-y-4 mb-8">
                <h2 className="text-3xl md:text-4xl font-bold">Lån i klarspråk</h2>
                <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                  De viktigaste guiderna för att förstå lån och privatekonomi
                </p>
              </div>
              <TopGuidesSection />
              
              {/* CTA - Se alla guider */}
              <div className="mt-16">
                <Card className="overflow-hidden border-2 hover:shadow-2xl transition-all duration-300">
                  <div className="grid lg:grid-cols-2 gap-8 lg:gap-12">
                    {/* Vänster kolumn - Text & CTA */}
                    <div className="p-8 lg:p-12 flex flex-col justify-center">
                      <div className="inline-flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                          <BookOpen className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-sm font-semibold text-primary uppercase tracking-wide">
                          Kunskapsbibliotek
                        </span>
                      </div>
                      
                      <h3 className="text-3xl lg:text-4xl font-bold mb-4">
                        Vill du lära dig mer om privatekonomi?
                      </h3>
                      
                      <p className="text-lg text-muted-foreground mb-8">
                        Utforska vårt omfattande bibliotek med 37 djupgående guider om lån och privatekonomi – från grundläggande begrepp till avancerad ekonomisk kunskap.
                      </p>
                      
                      <Button size="lg" onClick={() => navigate(`/${currentLanguage}/klarsprak`)} className="gap-2 text-lg group">
                        Utforska alla guider
                        <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </div>
                    
                    {/* Höger kolumn - Stats Grid */}
                    <div className="bg-gradient-to-br from-primary/5 to-primary/10 p-8 lg:p-12 flex items-center">
                      <div className="grid grid-cols-2 gap-6 w-full">
                        {/* Stat 1 */}
                        <div className="text-center p-6 bg-background/80 backdrop-blur rounded-2xl border border-primary/10">
                          <div className="text-4xl font-bold text-primary mb-2">37</div>
                          <div className="text-sm text-muted-foreground font-medium">
                            Guider
                          </div>
                        </div>
                        
                        {/* Stat 2 */}
                        <div className="text-center p-6 bg-background/80 backdrop-blur rounded-2xl border border-primary/10">
                          <div className="text-4xl font-bold text-primary mb-2">5</div>
                          <div className="text-sm text-muted-foreground font-medium">
                            Kategorier
                          </div>
                        </div>
                        
                        {/* Stat 3 */}
                        <div className="text-center p-6 bg-background/80 backdrop-blur rounded-2xl border border-primary/10">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            
                            <div className="text-2xl font-bold text-primary">Experttips</div>
                          </div>
                          <div className="text-sm text-muted-foreground font-medium">Granskad information</div>
                        </div>
                        
                        {/* Stat 4 */}
                        <div className="text-center p-6 bg-background/80 backdrop-blur rounded-2xl border border-primary/10">
                          <div className="flex items-center justify-center gap-2 mb-2">
                            <Clock className="w-5 h-5 text-primary" />
                            <div className="text-2xl font-bold text-primary">8</div>
                          </div>
                          <div className="text-sm text-muted-foreground font-medium">
                            Genomsnittlig lästid
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </section>
        </DeferredSection>

        {/* Trust Section - Load last */}
        <Suspense fallback={<div className="h-64 bg-muted/30 animate-pulse" />}>
          <DeferredSection delay={0} rootMargin="600px">
            <TrustSection />
          </DeferredSection>
        </Suspense>
      </main>
      
      <ScrollToTopButton />
      <Footer />
      <PWAInstallPrompt />
    </div>;
};
export default Index;